import { ApolloClient, InMemoryCache, Observable, createHttpLink, from } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GQL_URI } from "@config/index";
import { useMemo, useState } from "react";
import { getJWTToken } from "./auth.util";

export const useGQLClient = () => {
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [requestQueue, setRequestQueue] = useState<Array<(token: string) => void>>([]); // Global variables for managing token refresh

  const client = useMemo(() => {
    // Function to refresh the token
    const refreshToken = async (): Promise<string> => {
      if (!isRefreshing) {
        setIsRefreshing(true);

        try {
          const token = await getJWTToken();
          if (!token) {
            throw new Error();
          }
          // Resolve all queued requests with the new token
          requestQueue.forEach((callback) => callback(token));
          setRequestQueue([]); // Clear the queue
          return token;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Token refresh failed:", error);
          // Reject all queued requests with an error
          requestQueue.forEach((callback) => callback(""));
          setRequestQueue([]); // Clear the queue
        } finally {
          setIsRefreshing(false);
        }
      }

      // If a refresh is already in progress, return a promise that resolves when it's complete
      return new Promise((resolve) => {
        requestQueue.push((newToken) => {
          resolve(newToken);
        });
      });
    };

    // Create an HTTP link
    const httpLink = createHttpLink({
      uri: GQL_URI, // Replace with your GraphQL endpoint
    });

    // Create a context link to add the Authorization header
    const authLink = setContext(async (_, { headers }) => {
      try {
        // Fetch a valid access token
        const token = await getJWTToken();
        // Add the token to the Authorization header
        return {
          headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : undefined,
          },
        };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error retrieving access token:", error);
        return { headers };
      }
    });

    // Error handling link with Observable for retrying failed requests
    const errorLink = onError(({ graphQLErrors, operation, forward, networkError }) => {
      if (graphQLErrors) {
        for (const err of graphQLErrors) {
          if (err?.message === "Unauthorized") {
            // Handle the unauthenticated error by refreshing the token
            // If unauthenticated, refresh the token and retry the request
            return new Observable((observer) => {
              refreshToken()
                .then((newToken) => {
                  if (!newToken) {
                    // If token refresh failed, throw an error
                    observer.error(new Error("Unable to refresh token."));
                    return;
                  }

                  // Update the operation with the new token
                  operation.setContext(({ headers = {} }) => ({
                    headers: {
                      ...headers,
                      Authorization: `Bearer ${newToken}`,
                    },
                  }));

                  // Retry the request with the new token
                  forward(operation).subscribe({
                    next: observer.next.bind(observer),
                    error: observer.error.bind(observer),
                    complete: observer.complete.bind(observer),
                  });
                })
                .catch((error) => {
                  observer.error(error);
                });
            });
          }
        }
      }
      if (networkError) {
        // const { statusCode } = networkError as ServerError;
      }
    });

    return new ApolloClient({
      link: from([authLink, errorLink, httpLink]),
      cache: new InMemoryCache({
        typePolicies: {
          User: {
            keyFields: ["sub"],
          },
          Page: {
            keyFields: ["id"],
          },
          Question: {
            keyFields: ["id"],
          },
        },
      }),
    });
  }, [isRefreshing, requestQueue]);

  return client;
};
